import React from 'react';
import {
  Search,
  Plus,
  Filter,
  Download,
  Eye,
  Trash2,
  Upload,
  FileText,
  Calendar,
  User,
} from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { Document } from '../store/slices/documentsSlice';

const Documents: React.FC = () => {
  const { searchTerm, setSearchTerm, getFilteredDocuments } = useStore(
    useShallow(state => ({
      searchTerm: state.searchTerm,
      setSearchTerm: state.setSearchTerm,
      getFilteredDocuments: state.getFilteredDocuments,
    }))
  );

  const filteredDocuments = getFilteredDocuments();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Reviewed':
        return 'border-transparent bg-primary text-primary-foreground';
      case 'Pending':
        return 'border-transparent bg-secondary text-secondary-foreground';
      case 'New':
        return 'border-transparent bg-accent text-accent-foreground';
      default:
        return 'border-transparent bg-muted text-muted-foreground';
    }
  };

  const stats = {
    total: filteredDocuments.length,
    reviewed: filteredDocuments.filter(
      (doc: Document) => doc.status === 'Reviewed'
    ).length,
    pending: filteredDocuments.filter(
      (doc: Document) => doc.status === 'Pending'
    ).length,
    new: filteredDocuments.filter((doc: Document) => doc.status === 'New')
      .length,
  };

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-3 md:flex-row md:justify-between md:items-center'>
        <div>
          <h1 className='text-2xl font-bold'>Documents</h1>
          <p className='text-muted-foreground'>
            Manage patient documents and medical records
          </p>
        </div>
        <button className='inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2'>
          <Plus className='h-4 w-4 mr-2' />
          Upload Document
        </button>
      </div>

      {/* Search and Filters */}
      <div className='rounded-lg border bg-card text-card-foreground shadow-sm p-6'>
        <div className='flex flex-col sm:flex-row gap-4'>
          <div className='flex-1 relative'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            <input
              type='text'
              placeholder='Search documents by name, patient, or type...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-10'
            />
          </div>
          <select className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'>
            <option value=''>All Types</option>
            <option value='medical'>Medical Records</option>
            <option value='insurance'>Insurance</option>
            <option value='consent'>Consent Forms</option>
          </select>
          <button className='inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2'>
            <Filter className='h-4 w-4 mr-2' />
            Filter
          </button>
        </div>
      </div>

      {/* Documents Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
        {filteredDocuments.map((doc: Document) => (
          <div
            key={doc.id}
            className='rounded-lg border bg-card text-card-foreground shadow-sm hover:shadow-sm transition-shadow'
          >
            <div className='p-4'>
              <div className='flex justify-between items-start mb-3'>
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(doc.status)}`}
                >
                  {doc.status}
                </span>
                <div className='flex space-x-1'>
                  <button className='p-1 text-muted-foreground hover:text-foreground'>
                    <Eye className='h-4 w-4' />
                  </button>
                  <button className='p-1 text-muted-foreground hover:text-foreground'>
                    <Download className='h-4 w-4' />
                  </button>
                  <button className='p-1 text-muted-foreground hover:text-destructive'>
                    <Trash2 className='h-4 w-4' />
                  </button>
                </div>
              </div>

              <div className='space-y-2'>
                <h3 className='font-medium truncate'>{doc.name}</h3>
                <div className='flex items-center text-sm text-muted-foreground'>
                  <FileText className='h-3 w-3 mr-1' />
                  {doc.type}
                </div>
                <div className='flex items-center text-sm text-muted-foreground'>
                  <User className='h-3 w-3 mr-1' />
                  {doc.patient}
                </div>
                <div className='flex items-center text-sm text-muted-foreground'>
                  <Calendar className='h-3 w-3 mr-1' />
                  {doc.uploadDate}
                </div>
                <div className='text-sm text-muted-foreground'>{doc.size}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Upload Area */}
      <div className='rounded-lg border bg-card text-card-foreground shadow-sm p-6'>
        <div className='border-2 border-dashed border-border rounded-lg p-8 text-center'>
          <Upload className='mx-auto h-12 w-12 text-muted-foreground' />
          <div className='mt-4'>
            <p className='text-lg font-medium'>Upload Documents</p>
            <p className='text-muted-foreground'>
              Drag and drop files here, or click to browse
            </p>
          </div>
          <button className='mt-4 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2'>
            Choose Files
          </button>
          <p className='mt-2 text-xs text-muted-foreground'>
            Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
        <div className='rounded-lg border bg-card text-card-foreground shadow-sm p-6'>
          <div className='flex items-center'>
            <div className='p-2 bg-primary/10 rounded-lg'>
              <FileText className='h-6 w-6 text-primary' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>
                Total Documents
              </p>
              <p className='text-2xl font-semibold'>{stats.total}</p>
            </div>
          </div>
        </div>

        <div className='rounded-lg border bg-card text-card-foreground shadow-sm p-6'>
          <div className='flex items-center'>
            <div className='p-2 bg-primary/10 rounded-lg'>
              <FileText className='h-6 w-6 text-primary' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>
                Reviewed
              </p>
              <p className='text-2xl font-semibold'>{stats.reviewed}</p>
            </div>
          </div>
        </div>

        <div className='rounded-lg border bg-card text-card-foreground shadow-sm p-6'>
          <div className='flex items-center'>
            <div className='p-2 bg-secondary/10 rounded-lg'>
              <FileText className='h-6 w-6 text-secondary-foreground' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>
                Pending
              </p>
              <p className='text-2xl font-semibold'>{stats.pending}</p>
            </div>
          </div>
        </div>

        <div className='rounded-lg border bg-card text-card-foreground shadow-sm p-6'>
          <div className='flex items-center'>
            <div className='p-2 bg-accent/10 rounded-lg'>
              <FileText className='h-6 w-6 text-accent-foreground' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>New</p>
              <p className='text-2xl font-semibold'>{stats.new}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documents;
